import {Component, OnInit} from "@angular/core";
import {WorkoutsControllerService} from "../../core/api/controllers/workouts/workouts-controller.service";
import {NotificationService} from "../../core/services/notification.service";
import {GlobalPreferences} from "../../core/helpers/global-data";
import {WorkoutControllerService} from "../../core/api/controllers/workout-controller.service";
import {IGetWorkoutsPayload} from "../../core/api/controllers/workouts/data/IGetWorkoutsPayload";
import {TSelectionWorkout} from "../../shared/interfaces/IWorkout";
import {PageEvent} from "@angular/material/paginator";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../../core/services/auth.service";
import {MatDialog} from "@angular/material/dialog";
import {
  IPushWorkoutsToLocationDialogData,
  PushWorkoutsToLocationDialogComponent
} from "./push-workouts-to-location-dialog/push-workouts-to-location-dialog.component";
import {_pt} from "../../core/services/preview-text/helpers/preview-text.function";


@Component({
  selector: "app-workouts",
  templateUrl: "./workouts.component.html",
  styleUrls: ["./workouts.component.scss"],
  providers: [WorkoutsControllerService, WorkoutControllerService],
})
export class WorkoutsComponent implements OnInit {
  workouts: TSelectionWorkout[] = [];
  page: number = 0;
  itemsPerPage: number = 25;
  totalCount: number = 0;
  isLoading = false;

  constructor(
    private workoutsService: WorkoutsControllerService,
    private workoutController: WorkoutControllerService,
    private authService: AuthService,
    private notificationService: NotificationService,
    public global: GlobalPreferences,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe({
      next: (params) => {
        if(params['page'] && params['page'] !== this.page) this.page = params['page'];
        if(params['itemsPerPage'] && params['itemsPerPage'] !== this.itemsPerPage) this.itemsPerPage = params['itemsPerPage'];
        this.getWorkouts();
      }
    })
    this.getWorkouts();
  }


  getWorkouts() {
    this.isLoading = true;
    this.workoutsService.getWorkouts(this.createPayload()).subscribe(
      (response) => {
        this.totalCount = response.totalCount;
        this.workouts = response.data.map(workout => {
          return {
            ...workout,
            isSelected: false
          } as TSelectionWorkout;
        });
        this.isLoading = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.isLoading = false;
      }
    );
  }

  changePage(event: PageEvent): void {
    this.page = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.router.navigate([], {
      queryParams: {
        page: this.page,
        itemsPerPage: this.itemsPerPage
      },
      queryParamsHandling: 'merge'
    })
  }

  createPayload(): IGetWorkoutsPayload {
    return {
      page: this.page,
      itemsPerPage: this.itemsPerPage,
      locationId: AuthService.getSelectedLocationId(),
      isActive: true
    }
  }

  get isSuperAdmin(): boolean {
    return this.authService.checkTokenAccess("SuperAdmin");
  }

  deleteWorkout(workoutId: string) {
    this.workouts = this.workouts.filter(workout => workout.id !== workoutId);
    this.totalCount = Math.max(0, this.totalCount - 1);
  }

  openPushWorkoutsDialog(): void {
    const data: IPushWorkoutsToLocationDialogData = {
      workoutIds: this.selectedWorkouts.map(workout => workout.id)
    }
    this.dialog.open(PushWorkoutsToLocationDialogComponent, {
      panelClass: "location-picker-dialog-panel",
      maxHeight: '88vh',
      height: '88vh',
      width: "94vw",
      maxWidth: "880px",
      disableClose: true,
      data
    }).afterClosed().subscribe({
      next: res => {
        if(res) this.clearSelection();
      }
    })
  }

  get selectedWorkouts(): TSelectionWorkout[] {
    return this.workouts.filter(workout => workout.isSelected);
  }

  get isOneSelected(): boolean {
    return this.workouts.some(workout => workout.isSelected);
  }

  clearSelection(): void {
    this.workouts.forEach(workout => workout.isSelected = false);
  }

  /**
   * The page is server-paginated, so "select all" can only mean the workouts
   * actually loaded. Labelled accordingly in the template.
   */
  selectAllOnPage(): void {
    this.workouts.forEach(workout => workout.isSelected = true);
  }

  get allOnPageSelected(): boolean {
    return this.workouts.length > 0 && this.workouts.every(workout => workout.isSelected);
  }

  /**
   * Clinic accounts call these "protocols", so every noun on this page comes from
   * the preview-text variant rather than being hardcoded.
   */
  get workoutNoun(): string {
    return _pt("workout_title").toLowerCase();
  }

  get workoutNounPlural(): string {
    return _pt("workouts_title").toLowerCase();
  }

  countLabel(count: number): string {
    return count === 1 ? this.workoutNoun : this.workoutNounPlural;
  }

  get deletedWorkoutsLink(): string {
    // Derived rather than hardcoded to /workouts/deleted, so clinic accounts stay
    // on their own /protocols/... URL scheme.
    return `${_pt("sidebar_workouts_link")}/deleted`;
  }

  trackByWorkoutId(_index: number, workout: TSelectionWorkout): string {
    return workout.id;
  }

  protected readonly _pt = _pt;
}
